<template>
  <div class="detail-drawer">

    <!-- ── Alert Header ── -->
    <div v-if="alert" class="alert-header mb-5">
      <div class="flex items-start justify-between gap-3 mb-3">
        <div>
          <div class="flex items-center gap-2 mb-1">
            <el-tag :type="alertTypeTag(alert.type)" class="uppercase font-bold tracking-wider">
              {{ alertTypeBadge(alert.type).label }}
            </el-tag>
            <el-tag :type="alertStatusTag(alert.status)" class="uppercase font-bold tracking-wider">
              {{ alertStatusBadge(alert.status).label }}
            </el-tag>
          </div>
          <div class="text-xl font-black text-profissa-text-main">
            {{ alert.artisan_name || 'Artisan inconnu' }}
          </div>
          <div class="text-sm font-mono text-profissa-text-muted mt-0.5">
            {{ alert.artisan_phone || '' }}
          </div>
        </div>
        <div class="text-right shrink-0">
          <div class="text-xs text-profissa-text-muted uppercase font-bold tracking-widest">Détectée le</div>
          <div class="text-sm text-profissa-text-main font-semibold mt-0.5">{{ fmtDate(alert.created_at) }}</div>
        </div>
      </div>

      <!-- Action buttons -->
      <div v-if="canResolve && isActionable(alert.status)" class="flex gap-2">
        <el-button
          type="info"
          size="small"
          :loading="isMutating"
          class="flex-1"
          @click="$emit('resolve', { id: alert.id, status: 'dismissed' })"
        >
          Faux positif
        </el-button>
        <el-button
          type="danger"
          size="small"
          :loading="isMutating"
          class="flex-1"
          @click="$emit('resolve', { id: alert.id, status: 'confirmed' })"
        >
          Confirmer fraude
        </el-button>
      </div>
      <div v-if="canSanction && alert.artisan_id && alert.status === 'confirmed'" class="mt-2">
        <el-button
          type="warning"
          size="small"
          class="w-full"
          @click="$emit('sanction-artisan', { artisanId: alert.artisan_id, artisanName: alert.artisan_name })"
        >
          Sanctionner l'artisan
        </el-button>
      </div>
    </div>

    <el-divider v-if="alert" class="!my-4" />

    <!-- ── AI Diagnostic Card ── -->
    <div v-if="parsedDetails" class="ai-card mb-5">
      <div class="ai-card-header">
        <el-icon class="text-profissa-accent"><MagicStick /></el-icon>
        <span>Diagnostic Automatique (IA)</span>
      </div>

      <!-- Confidence -->
      <div v-if="parsedDetails.confidence !== undefined" class="mb-3">
        <div class="flex items-center justify-between mb-1">
          <span class="text-xs font-black uppercase tracking-widest text-profissa-text-muted">Indice de confiance</span>
          <span class="font-black text-sm" :style="{ color: confColor(parsedDetails.confidence) }">
            {{ Math.round(parsedDetails.confidence * 100) }}%
          </span>
        </div>
        <el-progress
          :percentage="Math.round(parsedDetails.confidence * 100)"
          :color="confColor(parsedDetails.confidence)"
          :show-text="false"
          :stroke-width="8"
        />
      </div>

      <!-- Price deviation -->
      <div v-if="parsedDetails.deviation_pct !== undefined" class="info-row">
        <span class="info-label">Écart tarifaire</span>
        <span class="text-red-500 font-black">+{{ Math.round(parsedDetails.deviation_pct) }}%</span>
        <span v-if="parsedDetails.item" class="text-profissa-text-muted text-xs"> · {{ parsedDetails.item }}</span>
      </div>

      <!-- Flags -->
      <div v-if="parsedDetails.flags?.length" class="mb-3">
        <div class="text-xs font-black uppercase tracking-widest text-profissa-text-muted mb-1.5">Signaux détectés</div>
        <div class="flex flex-wrap gap-1.5">
          <el-tag
            v-for="flag in parsedDetails.flags"
            :key="flag"
            type="warning"
            class="font-bold !text-xs"
          >{{ flag }}</el-tag>
        </div>
      </div>

      <!-- Reasoning -->
      <div v-if="parsedDetails.reasoning" class="reasoning-box">
        <div class="text-xs font-black uppercase tracking-widest text-profissa-text-muted mb-1.5">Analyse détaillée</div>
        <p class="text-sm text-profissa-text-main leading-relaxed">{{ parsedDetails.reasoning }}</p>
      </div>
    </div>

    <!-- ── Events Timeline ── -->
    <div class="events-section">
      <div class="text-xs font-black uppercase tracking-widest text-profissa-text-muted mb-3">
        Historique des actions
      </div>

      <div v-if="!events.length" class="text-sm text-profissa-text-dim italic text-center py-4">
        Aucun événement enregistré
      </div>

      <el-timeline v-else class="premium-timeline py-2">
        <el-timeline-item
          v-for="(evt, i) in visibleEvents"
          :key="i"
          :timestamp="fmtDate(evt.created_at)"
          :type="getEvtType(evt.from_status, evt.to_status)"
          placement="top"
        >
          <div class="event-card">
            <div class="text-xs font-black uppercase tracking-widest text-profissa-accent mb-1">
              {{ statusLabel(evt.from_status) }} → {{ statusLabel(evt.to_status) }}
            </div>

            <!-- If notes is JSON (initial AI event), parse and show nicely -->
            <template v-if="parseNotes(evt)">
              <div v-if="parseNotes(evt).reasoning" class="text-sm text-profissa-text-main leading-relaxed">
                {{ parseNotes(evt).reasoning }}
              </div>
              <div v-if="parseNotes(evt).flags?.length" class="flex flex-wrap gap-1 mt-1.5">
                <el-tag
                  v-for="flag in parseNotes(evt).flags"
                  :key="flag"
                  type="warning"
                  size="small"
                  class="!text-[0.6rem]"
                >{{ flag }}</el-tag>
              </div>
            </template>
            <template v-else>
              <div v-if="evt.notes" class="text-sm text-profissa-text-main leading-relaxed">{{ evt.notes }}</div>
            </template>

            <div v-if="evt.reason_code" class="mt-1.5 text-xs text-profissa-text-muted font-semibold">
              Motif : {{ reasonLabel(evt.reason_code) }}
            </div>
          </div>
        </el-timeline-item>
      </el-timeline>
    </div>

  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import { MagicStick } from '@element-plus/icons-vue'
import { alertTypeBadge, alertStatusBadge, fmtDate } from '../../utils/labels'

const props = defineProps({
  alert:    { type: Object,  default: null },
  events:   { type: Array,   default: () => [] },
  canResolve:  { type: Boolean, default: false },
  canSanction: { type: Boolean, default: false },
  isMutating:  { type: Boolean, default: false },
})

defineEmits(['resolve', 'sanction-artisan'])

const STATUS_LABELS = {
  open: 'Ouverte', investigating: 'En enquête', confirmed: 'Confirmée', dismissed: 'Classée',
}
const REASON_LABELS = {
  verified_evidence: 'Preuves vérifiées',
  false_positive: 'Faux positif',
  insufficient_evidence: 'Preuves insuffisantes',
}

const statusLabel = (s) => STATUS_LABELS[s] ?? s
const reasonLabel = (r) => REASON_LABELS[r] ?? r

const alertTypeTag = (t) => ({
  photo_anomaly: 'danger', price_anomaly: 'danger',
  fake_cancellation: 'warning',
}[t] || 'info')

const alertStatusTag = (s) => ({
  open: 'danger', investigating: 'warning', confirmed: 'danger', dismissed: 'info'
}[s] || 'info')

const isActionable = (status) => status === 'open' || status === 'investigating'

const confColor = (v) => v >= 0.75 ? 'var(--el-color-danger)' : v >= 0.5 ? 'var(--el-color-warning)' : 'var(--el-color-success)'

const parsedDetails = computed(() => {
  if (!props.alert?.details) return null
  try {
    const d = typeof props.alert.details === 'string'
      ? JSON.parse(props.alert.details)
      : props.alert.details
    return (d && typeof d === 'object') ? d : null
  } catch { return null }
})

const parseNotes = (evt) => {
  if (!evt.notes) return null
  try {
    const d = typeof evt.notes === 'string' ? JSON.parse(evt.notes) : evt.notes
    return (d && typeof d === 'object' && (d.reasoning || d.flags)) ? d : null
  } catch { return null }
}

// Skip the initial open→open AI event in the timeline if we already show it in the diagnostic card above
const visibleEvents = computed(() => {
  if (!parsedDetails.value) return props.events
  return props.events.filter(evt => {
    if (evt.from_status === 'open' && evt.to_status === 'open') {
      const parsed = parseNotes(evt)
      return !parsed  // hide if it's pure AI JSON (already shown in diagnostic card)
    }
    return true
  })
})

const getEvtType = (from, to) => {
  if (to === 'confirmed')     return 'danger'
  if (to === 'investigating') return 'warning'
  if (to === 'dismissed')     return 'info'
  if (from === 'open' && to === 'open') return 'primary'
  return 'success'
}
</script>

<style scoped>
.detail-drawer { padding: 0.25rem; }

.alert-header {
  @apply rounded-2xl p-4 border border-profissa-glass-border bg-white/5;
}

.ai-card {
  @apply rounded-2xl p-4 border border-profissa-glass-border bg-white/5;
}
.ai-card-header {
  @apply flex items-center gap-2 text-sm font-black uppercase tracking-widest text-profissa-accent mb-3;
}

.info-row {
  @apply flex items-center gap-2 text-sm mb-3;
}
.info-label {
  @apply text-profissa-text-muted font-semibold text-xs uppercase tracking-wide mr-auto;
}

.reasoning-box {
  @apply rounded-xl p-3 bg-white/5 border border-profissa-glass-border;
}

.events-section { }

.event-card {
  @apply p-3 rounded-xl bg-white/5 border border-profissa-glass-border;
}

:deep(.el-timeline-item__timestamp) {
  @apply text-[0.7rem] font-bold text-profissa-text-muted uppercase tracking-tighter !important;
}
:deep(.el-timeline-item__node) {
  @apply bg-profissa-bg border-2 border-profissa-glass-border !important;
}
</style>
